import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import { Search } from "lucide-react";
import { Input } from "components/ui/input";
import { usePeopleApi } from "features/People/api";
import { cn } from "lib/utils";

type SearchInputProps = {
    className?: string;
};

function SearchInput({ className }: SearchInputProps) {
    const navigate = useNavigate();
    const [search, setSearch] = useState("");

    const { fetch, data } = usePeopleApi();

    const onSearchChange = useMemo(
        () =>
            debounce((e: React.ChangeEvent<HTMLInputElement>) => {
                setSearch(e.target.value);
            }, 500),
        [setSearch]
    );

    useEffect(() => {
        return () => onSearchChange.cancel();
    }, [onSearchChange]);

    useEffect(() => {
        const controller = new AbortController();

        if (search !== "") {
            fetch({ page: 1, perPage: 10, search }, controller);
        }

        return () => controller.abort();
    }, [fetch, search]);

    const onOptionClick = useCallback(
        (id: string) => {
            navigate(`/people/${id}/job-pay`);
            setSearch("");
        },
        [navigate]
    );

    const onMouseLeave = useCallback(
        () =>
            setTimeout(() => {
                setSearch("");
            }, 1000),
        []
    );

    return (
        <div className={cn("tw-relative tw-w-full tw-max-w-sm", className)} onMouseLeave={onMouseLeave}>
            <div className="tw-relative tw-flex tw-items-center">
                <Search className="tw-pointer-events-none tw-absolute tw-left-3 tw-size-4 tw-text-muted-foreground" />
                <Input className="tw-bg-background tw-pl-9" onChange={onSearchChange} />
            </div>

            {search !== "" && data?.length > 0 && (
                <div className="tw-absolute tw-top-full tw-z-50 tw-mt-1 tw-flex tw-w-full tw-flex-col tw-gap-1 tw-rounded-md tw-border tw-bg-popover tw-p-1 tw-text-popover-foreground tw-shadow-md">
                    {data?.map((user: { id: string; firstName: string; lastName: string }) => (
                        <span
                            key={user.id}
                            className="tw-cursor-pointer tw-rounded tw-px-2 tw-py-1 tw-text-sm hover:tw-bg-accent hover:tw-text-accent-foreground"
                            onClick={() => onOptionClick(user.id)}
                        >
                            {user && `${user.firstName} ${user.lastName}`}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

export default SearchInput;
